/* eslint-disable max-len */
import { mockData } from './mockData';
import { getNewPlaConfiguredPrice } from '../api/plaConfigurationApi';
import logNewRelicCustom, { PLA_EXPERIENCE_EXPELLED, PLA_MARKET_STORE_LOCALIZED } from './logNewRelicCustom';
import {
  Utils,
  QUERY_PARAM_BASE_CONFIGURATION_ID_FOR_SELECTIONS,
  QUERY_PARAM_CONFIGURATION_ID_FOR_SELECTIONS,
  QUERY_PARAM_PLA_CONFIGURATION_GUID,
  QUERY_PARAM_REGION_ID,
  QUERY_OMS_ID
} from './urlUtils';
import { resetStateToInitial } from '../actions/plaConfigurations';

export const formPlaApiCreatePayload = (options, plaConfiguration) => {
  const { userHasChangedColor, color } = plaConfiguration;
  return options.flatMap((option) => {
    const selectedChoices = option.choices.filter((choice) => choice.isSelected).map((choice) => {
      if (userHasChangedColor && option.optionType === 'Primary Color') {
        return {
          optionId: option.id,
          choiceValue: color.id,
          choiceName: color.name,
          optionType: option.optionType,
          optionTags: option.tags
        };
      }
      return {
        optionId: option.id,
        choiceValue: choice.id,
        choiceName: choice.value,
        optionType: option.optionType,
        optionTags: option.tags
      };
    });

    const dependentChoices = option.choices.flatMap((choice) => {
      if (choice.dependentOptions && choice.dependentOptions.length > 0) {
        return formPlaApiCreatePayload(choice.dependentOptions, plaConfiguration);
      }
      return [];
    });

    return [...selectedChoices, ...dependentChoices];
  });
};

export const formPlaApiPricingPayload = (choices, color) => {

  const transformedChoices = choices.map((choice) => {
    const choiceColor = choice.choice.tags.find((tag) => tag.includes('filter:color'));
    const storeChoice = choice.choice.tags.find((tag) => tag.includes('store-number'));
    const previousSelectedSku = choice.choice.tags.find((tag) => tag.includes('surface-sku'));

    if (choiceColor) {
      return {
        choiceId: color.id,
        optionId: choice.option.id,
        choiceValue: Number(color.name),
        tags: color.tags,
        optionTags: choice.option.tags,
      };
    }
    if (storeChoice || previousSelectedSku) {
      return {};
    }
    return {
      choiceId: choice.choice.id,
      optionId: choice.option.id,
      choiceValue: Number(choice.choice.value),
      tags: choice.choice.tags,
      optionTags: choice.option.tags,
    };

  });
  return transformedChoices;
};

export const findPlaColor = (plaConfigurationDetails) => {
  const allChoices = plaConfigurationDetails.configuration.choices;

  const primaryColorChoice = allChoices.find((choice) => choice.option.optionType === 'Primary Color');

  return primaryColorChoice.choice;
};

export const findPlaSurfaceCode = (plaConfigurationDetails) => {
  const allChoices = plaConfigurationDetails.configuration.choices;

  const plaSurfaceCode = allChoices.find((choice) => choice.option.name === 'Surface Code');

  return plaSurfaceCode.choice.value;
};

export const findPlaProductId = (plaConfigurationDetails) => {
  const plaProductId = plaConfigurationDetails.configuration.product.id;

  return plaProductId;
};

export const findPlaSquareFeet = (plaConfigurationDetails) => {

  const allChoices = plaConfigurationDetails.configuration.choices;

  const plaSquareFootage = allChoices.find((choice) => choice.option.internalName === 'Countertops_TotalSquareFeet');

  return plaSquareFootage.choice.value;
};

export const findPlaSelectedThickness = (plaConfigurationDetails) => {

  const allChoices = plaConfigurationDetails.configuration.choices;

  const plaSelectedThickness = allChoices.find((choice) => choice.option.internalName === 'Countertops_SurfaceThickness');

  return parseFloat(plaSelectedThickness.choice.value);

};

export const verifySelectedSkuForPla = (json, sku) => {
  const surfaceSkuObject = json.selections.find((selection) => selection.optionTags.includes('surface-sku'));

  if (surfaceSkuObject) {
    if (surfaceSkuObject.choiceName !== sku) {
      surfaceSkuObject.choiceName = sku;
    }
  }
  return json;

};

export const formatSelectionsForPlaPipPricing = (choices, storeId) => {
  return choices.map((choice) => {
    const storeChoice = choice.choice.tags.find((tag) => tag.includes('store-number'));

    if (storeChoice) {
      return {
        choiceId: choice.choice.id,
        optionId: choice.option.id,
        choiceValue: storeId,
        tags: choice.choice.tags,
        optionTags: choice.option.tags
      };
    }

    return {
      choiceId: choice.choice.id,
      optionId: choice.option.id,
      choiceValue: Number.isNaN(choice.choice.value) ? choice.choice.value : Number(choice.choice.value),
      tags: choice.choice.tags,
      optionTags: choice.option.tags
    };
  });

};

export const plaPipPricing = async (plaConfigurationDetails, storeId, selectedSku, productId, availableSkus) => {
  const formattedChoices = formatSelectionsForPlaPipPricing(plaConfigurationDetails.configuration.choices, storeId);
  const { siteId, productLineId } = window.injectedConfiguratorSettings;

  const payload = {
    Siteid: siteId,
    ProductId: productId,
    choices: formattedChoices,
    storeId,
    availableSkus,
    ProductLineId: productLineId,
    selectedSku,
  };

  const response = await getNewPlaConfiguredPrice(payload);

  return response;
};

export const getPlaConfiguredPrice = (plaConfiguredPrice) => {
  if (plaConfiguredPrice?.StrikeThroughPriceModel?.HasStrikeThroughPricing) {
    return plaConfiguredPrice?.StrikeThroughPriceModel?.Price;
  }
  return plaConfiguredPrice?.AsConfigured;
};

export const plaDetailPriceResponseFormatter = (plaConfiguredPrice) => {
  if (plaConfiguredPrice !== null && typeof plaConfiguredPrice === 'object') {
    if (Array.isArray(plaConfiguredPrice)) {
      return plaConfiguredPrice.map((item) => plaDetailPriceResponseFormatter(item));
    }
    return Object.keys(plaConfiguredPrice).reduce((acc, key) => {
      const newKey = key.charAt(0).toUpperCase() + key.slice(1);
      const value = plaConfiguredPrice[key];
      acc[newKey] = plaDetailPriceResponseFormatter(value);
      return acc;
    }, {});

  }
  return plaConfiguredPrice;
};

export const plaConfigurationDetailsFormatter = (plaConfigurationDetails) => {
  let updatedDetails = { ...plaConfigurationDetails };

  let configurations = updatedDetails.configurations;

  if (Array.isArray(configurations) && configurations.length > 0) {
    let configuration = configurations[0];

    delete updatedDetails.configurations;
    updatedDetails.configuration = configuration;

    return updatedDetails;
  }
  // Return the original plaConfigurationDetails if no modifications were made
  return plaConfigurationDetails;
};

// TODO this can be removed as soon as the new endpoint is in prod. It only accounts for small window where url could receive old configurations shape
export const waitForAvailableSkus = (store) => new Promise((resolve) => {
  const checkInterval = setInterval(() => {
    const availableSkus = store.getState().countertops.availableSkus;
    if (availableSkus) {
      clearInterval(checkInterval);
      resolve(availableSkus);
    }
  }, 100);
});

export const forcePlaExitIfOutOfRegion = (
  regionId,
  newStoreId,
  previousStore,
  plaId,
  configurationGuid,
  storeNumbers = [],
  dispatch,
  newStoreZipcode,
  addVewParamList = false
) => {
  const url = window?.location?.href;
  if ((storeNumbers.length !== 0) && !storeNumbers.includes(newStoreId.toString())) {
    dispatch(resetStateToInitial());
    logNewRelicCustom(PLA_EXPERIENCE_EXPELLED, {
      url, regionId, previousStore, newLocalizedStore: newStoreId, plaId, configurationGuid
    });

    const deleteParamList = [
      QUERY_PARAM_BASE_CONFIGURATION_ID_FOR_SELECTIONS,
      QUERY_PARAM_CONFIGURATION_ID_FOR_SELECTIONS,
      QUERY_PARAM_PLA_CONFIGURATION_GUID,
      QUERY_PARAM_REGION_ID,
      QUERY_OMS_ID
    ];

    let newParamList;
    /* eslint-disable-next-line max-len */
    newParamList = addVewParamList ? [{ name: 'storeId', value: newStoreId }, { name: 'zipcode', value: newStoreZipcode }] : [];

    const newHistory = `${window.location.pathname}${Utils.deleteAddQueryParameters(deleteParamList, newParamList)}`;
    window.history.replaceState({}, document.title, newHistory);
    window.location.reload(true);
  }
};